/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Phone, Mail, Building2, Clock, RefreshCw, Edit2, Trash2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { format } from 'date-fns';
import { formatET } from '../utils/timeUtils';
import { useAuth } from '../contexts/AuthContext';
import { PhoneClickable } from '../components/PhoneQRModal';

const statusColors = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const GhostAppointmentsPage = () => {
  const { canBulkDelete } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  
  // Selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState('selected'); // 'selected' or 'all'
  const [deleteLoading, setDeleteLoading] = useState(false);
  
  // Reschedule dialog state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newScheduledTime, setNewScheduledTime] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  useEffect(() => {
    fetchAppointments();
  }, []);

  // Handle select all toggle
  useEffect(() => {
    if (selectAll) {
      setSelectedIds(appointments.map(a => a.id));
    } else if (selectedIds.length === appointments.length && appointments.length > 0) {
      // If all were selected and selectAll toggled off, clear
    }
  }, [selectAll]);

  const fetchAppointments = async () => {
    try {
      const response = await api.get('/ghost/appointments');
      setAppointments(response.data.appointments || []);
      setSelectedIds([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Failed to fetch appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (appointmentId, newStatus) => {
    setUpdating(appointmentId);
    try {
      await api.patch(`/ghost/appointments/${appointmentId}`, null, {
        params: { status: newStatus }
      });
      toast.success(`Appointment marked as ${newStatus}`);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const toggleSelection = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(appointments.map(a => a.id));
      setSelectAll(true);
    }
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setSelectAll(false);
  };

  const openDeleteDialog = (mode) => {
    setDeleteMode(mode);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      if (deleteMode === 'all') {
        await api.delete('/ghost/appointments/clear-all');
        toast.success('All appointments cleared');
      } else {
        await api.delete('/ghost/appointments/bulk-delete', { data: { ids: selectedIds } });
        toast.success(`Deleted ${selectedIds.length} appointments`);
      }
      setDeleteDialogOpen(false);
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to delete appointments');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openRescheduleDialog = (appointment) => {
    setSelectedAppointment(appointment);
    setNewScheduledTime(appointment.scheduled_time || '');
    setRescheduleDialogOpen(true);
  };

  const handleReschedule = async () => {
    if (!selectedAppointment || !newScheduledTime.trim()) {
      toast.error('Please enter a new time');
      return;
    }

    setRescheduleLoading(true);
    try {
      await api.patch(`/ghost/appointments/${selectedAppointment.id}/reschedule`, {
        scheduled_time: newScheduledTime.trim()
      });
      toast.success('Appointment rescheduled successfully');
      setRescheduleDialogOpen(false);
      setSelectedAppointment(null);
      setNewScheduledTime('');
      fetchAppointments();
    } catch (error) {
      toast.error('Failed to reschedule appointment');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const pendingCount = appointments.filter(a => a.status === 'pending').length;
  const confirmedCount = appointments.filter(a => a.status === 'confirmed').length;
  const completedCount = appointments.filter(a => a.status === 'completed').length;

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Ghost Appointments</h1>
          <p className="text-sm text-slate-500">Leads captured and scheduled by Ghost</p>
        </div>
        <div className="flex gap-2">
          {canBulkDelete() && (
            <Button 
              variant="destructive" 
              onClick={() => openDeleteDialog('all')}
              disabled={appointments.length === 0}
              data-testid="clear-all-appointments-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </Button>
          )}
          <Button variant="outline" onClick={fetchAppointments} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-slate-800">{appointments.length}</div>
            <div className="text-sm text-slate-500">Total Appointments</div>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-amber-700">{pendingCount}</div>
            <div className="text-sm text-amber-600">Pending</div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-blue-700">{confirmedCount}</div>
            <div className="text-sm text-blue-600">Confirmed</div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="text-2xl font-bold text-green-700">{completedCount}</div>
            <div className="text-sm text-green-600">Completed</div>
          </CardContent>
        </Card>
      </div>

      {/* Selection Actions Bar */}
      {canBulkDelete() && appointments.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-slate-50 rounded-lg border">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Checkbox 
                id="select-all"
                checked={selectAll}
                onCheckedChange={handleSelectAll}
                data-testid="select-all-checkbox"
              />
              <Label htmlFor="select-all" className="text-sm cursor-pointer">
                Select All
              </Label>
            </div>
            {selectedIds.length > 0 && (
              <span className="text-sm text-slate-600">
                {selectedIds.length} selected
              </span>
            )}
          </div>
          <div className="flex gap-2">
            {selectedIds.length > 0 && (
              <>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearSelection}
                  data-testid="clear-selection-btn"
                >
                  Clear Selection
                </Button>
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => openDeleteDialog('selected')}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete Selected
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Appointments List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">All Appointments</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Loading...</div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-8 text-slate-500">No appointments yet</div>
          ) : (
            <div className="space-y-4">
              {appointments.map((appt) => (
                <div
                  key={appt.id}
                  className={`p-4 border rounded-lg hover:shadow-sm transition-shadow ${
                    selectedIds.includes(appt.id) ? 'border-blue-400 bg-blue-50' : 'border-slate-200'
                  }`}
                  data-testid={`ghost-appointment-${appt.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      {canBulkDelete() && (
                        <Checkbox
                          checked={selectedIds.includes(appt.id)}
                          onCheckedChange={() => toggleSelection(appt.id)}
                          data-testid={`checkbox-${appt.id}`}
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-slate-800">{appt.name || 'Unknown'}</span>
                          <Badge className={statusColors[appt.status] || statusColors.pending}>
                            {appt.status}
                          </Badge>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600">
                          {appt.company && (
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-slate-400" />
                              {appt.company}
                            </div>
                          )}
                          {appt.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-slate-400" />
                              <PhoneClickable phone={appt.phone} name={appt.name || appt.client_name} showIcon={false} />
                            </div>
                          )}
                          {appt.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-slate-400" />
                              <a href={`mailto:${appt.email}`} className="hover:text-blue-600">{appt.email}</a>
                            </div>
                          )}
                          {appt.scheduled_time && (
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4 text-slate-400" />
                              {appt.scheduled_time}
                            </div>
                          )}
                        </div>
                        
                        {appt.created_at && (
                          <div className="text-xs text-slate-400 mt-2">
                            Created: {formatET(appt.created_at, 'MMM d, yyyy h:mm a')}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Quick Actions */}
                    <div className="flex flex-col gap-2">
                      {appt.phone && (
                        <div data-testid={`call-btn-${appt.id}`}>
                          <PhoneClickable
                            phone={appt.phone}
                            name={appt.name || appt.client_name}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 hover:text-white no-underline transition-colors"
                            showIcon={true}
                          />
                        </div>
                      )}
                      
                      <button
                        onClick={() => openRescheduleDialog(appt)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        data-testid={`reschedule-btn-${appt.id}`}
                      >
                        <Edit2 className="h-3 w-3" />
                        Reschedule
                      </button>
                      
                      <Select
                        value={appt.status}
                        onValueChange={(value) => updateStatus(appt.id, value)}
                        disabled={updating === appt.id}
                      >
                        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`status-select-${appt.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {deleteMode === 'all' 
                ? `You are about to permanently delete ${appointments.length} appointment records.`
                : `You are about to permanently delete ${selectedIds.length} selected records.`
              }
              <br /><span className="font-medium text-red-600">This cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={deleteLoading}
              data-testid="confirm-delete-btn"
            >
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleDialogOpen} onOpenChange={setRescheduleDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule Appointment</DialogTitle>
          </DialogHeader>
          
          {selectedAppointment && (
            <div className="space-y-4 py-4">
              <div className="text-sm text-slate-600">
                <span className="font-medium">{selectedAppointment.name}</span>
                {selectedAppointment.company && (
                  <span className="text-slate-500"> • {selectedAppointment.company}</span>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="current-time" className="text-xs text-slate-500">Current Time</Label>
                <div className="text-sm text-slate-700 bg-slate-50 px-3 py-2 rounded border">
                  {selectedAppointment.scheduled_time || 'Not set'}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="new-time">New Time</Label>
                <Input
                  id="new-time"
                  value={newScheduledTime}
                  onChange={(e) => setNewScheduledTime(e.target.value)}
                  placeholder="e.g., Tomorrow morning, Friday 2pm"
                  data-testid="reschedule-time-input"
                />
                <p className="text-xs text-slate-500">
                  Enter the new preferred time (e.g., "Tomorrow afternoon", "Monday 10am")
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRescheduleDialogOpen(false)}
              disabled={rescheduleLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReschedule}
              disabled={rescheduleLoading || !newScheduledTime.trim()}
              data-testid="reschedule-confirm-btn"
            >
              {rescheduleLoading ? 'Saving...' : 'Reschedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GhostAppointmentsPage;
